import { useMutation, useApolloClient } from '@apollo/client'
import { Formik } from 'formik'
import { isEmpty, isString } from 'lodash'
import { useState, useRef, ReactElement, ChangeEvent, ReactNode } from 'react'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Select } from 'components/v2/Select'
import { Textarea } from 'components/v2/Textarea'
import { cancelBookingReasonMessages } from 'features/Bookings/components/CancelBookingReason/messages'
import {
  GTBookingTicketCancelationReason,
  GTBookingV2,
  GTBookingV2Status,
  GTBookingV2TicketStatus,
  GTBookingV2Vertical,
  GTCancelBookingTicketsInput,
  GTCancelTicketsInput,
  GTRefundDestinationInput
} from 'gql/types.generated'
import { resolvers as bookingResolvers } from 'resolvers/bookings'
import { getUnprocessableErrors } from 'utils/error'
import { safeFormatMessage } from 'utils/intl'
import { round, sanitizePrice } from 'utils/math'
import { PartialCancellation, PartialCancellationV2 } from './Partial'
import { TotalCancellation } from './Total'
import { amountsAreMatching, schema } from './schema'
import { ITabProps, ICancelTicketsModalProps, ICancellationData } from './types'
import {
  ISelectRefundDestinationProps,
  SelectRefundDestination
} from 'features/Bookings/components/CancelTicketsModal/SelectRefundDestination'
import { Column } from 'components/v2/Column'
import { Row } from 'components/v2/Row'
import {
  ADD_REFUNDS_TO_BOOKING,
  UPDATE_BOOKING_STATUS,
  UPDATE_TICKET_STATUS,
  CANCEL_TICKETS,
  CANCEL_TICKETS_V2
} from 'features/Bookings/mutations-legacy'
import { useFeatureFlag } from '@10xlabs/hooks'
import { EFeatureFlagsNames } from 'config/feature-flags'
import { getPaidPayments, getPaidPaymentsV2, mapBookingToCancelBookingForm } from 'features/Bookings/utils'
import { GET_BOOKINGS } from 'features/Bookings/queries-legacy'

const ModalContainer = styled(Modal.Container)`
  max-width: 600px;
`

const ModalHeader = styled(Modal.Header)`
  border-bottom: none;
`

const Tabs = styled.div<TSFixMe>`
  border-bottom: 1px solid rgba(153, 153, 189, 0.3);
  display: flex;
  padding: 0 20px;
  margin-bottom: 20px;
`

const Tab = styled.div<ITabProps>`
  cursor: pointer;
  font-size: 15px;
  border-bottom: ${({ isActive }): string => (isActive ? '3px solid #80deea' : 'none')};
  padding: ${({ isActive }): string => (isActive ? '0 10px 7px' : '0 10px 10px')};
  font-weight: ${({ isActive }): string => (isActive ? '500' : 'normal')};
`

const CANCELLATION_TOTAL_INDEX = 0
const CANCELLATION_PARTIAL_INDEX = 1

const getUncancelledTicketsIds = (booking: GTBookingV2): string[] => {
  return booking.tickets
    .filter(({ status }): TSFixMe => status !== GTBookingV2TicketStatus.Cancelled)
    .map(({ id }): TSFixMe => id)
}

const messages = defineMessages({
  reasonPlaceholder: {
    id: 'booking.cancel.reason.placeholder',
    defaultMessage: 'Select a reason'
  }
})

const CANCEL_BOOKING_REASONS = [
  GTBookingTicketCancelationReason.Fraud,
  GTBookingTicketCancelationReason.NoShow,
  GTBookingTicketCancelationReason.TripChange,
  GTBookingTicketCancelationReason.Personal,
  GTBookingTicketCancelationReason.Operational,
  GTBookingTicketCancelationReason.Test
]
// TODO: Refactor this component when the feature flag is removed https://kolors.atlassian.net/browse/NA-17493
export const CancelTicketsModal = ({ booking, closeModal }: ICancelTicketsModalProps): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  // @ts-expect-error ts(2322) FIXME
  client.addResolvers(bookingResolvers)
  const isPosBooking = booking.salesChannel.id === process.env.POS_MX_SALES_CHANNEL_ID

  const [isNewBookingCancellationFeatureFlagEnabled] = useFeatureFlag(EFeatureFlagsNames.BookingCancellationV2)
  const isNewCancellationFlow = isNewBookingCancellationFeatureFlagEnabled && !isPosBooking

  const [tabIndex, setTabIndex] = useState(CANCELLATION_TOTAL_INDEX)
  const [comments, setComments] = useState('')
  const cancellationData = useRef<ICancellationData>()

  const [addRefundsToBooking] = useMutation(ADD_REFUNDS_TO_BOOKING)
  const [updateBookingStatus] = useMutation(UPDATE_BOOKING_STATUS)
  const [updateTicketStatus] = useMutation(UPDATE_TICKET_STATUS)
  const [cancelTickets, cancelTicketsStatus] = useMutation<boolean, { input: GTCancelTicketsInput }>(CANCEL_TICKETS, {
    onCompleted() {
      const isPartiallyCancelled =
        getUncancelledTicketsIds(booking).length !== cancellationData.current?.selectedTicketsIds.length

      if (!isPartiallyCancelled) {
        updateBookingStatus({
          variables: {
            status: GTBookingV2Status.Cancelled,
            bookingId: booking.id
          }
        })
      }
      cancellationData.current?.selectedTicketsIds.forEach((id): TSFixMe => {
        updateTicketStatus({
          variables: {
            status: GTBookingV2TicketStatus.Cancelled,
            ticketId: id
          }
        })
      })
      addRefundsToBooking({
        variables: {
          bookingId: booking.id,
          refunds: cancellationData.current?.selectedPayments.map((selectedPayment): TSFixMe => {
            return {
              paymentId: selectedPayment.paymentId,
              amount: sanitizePrice(selectedPayment.amountToRefund * 100)
            }
          })
        }
      })
      closeModal()
    }
  })
  const [cancelTicketsV2Loading, setCancelTicketsV2Loading] = useState(false)
  const [cancelTicketsV2, cancelTicketsV2Status] = useMutation<boolean, { input: GTCancelBookingTicketsInput }>(
    CANCEL_TICKETS_V2,
    {
      onCompleted() {
        setTimeout(() => {
          client.refetchQueries({
            include: [GET_BOOKINGS]
          })
          setCancelTicketsV2Loading(false)
          closeModal()
        }, 5000)
      }
    }
  )
  const paidPayments = isNewCancellationFlow ? getPaidPaymentsV2(booking) : getPaidPayments(booking)
  const hasPaidPayments = paidPayments.length > 0

  const handleCommentsChange = ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>): void => {
    setComments(value)
  }
  const handleSubmitV2 = (values: ICancellationData): void => {
    cancellationData.current = values
    let input: GTCancelBookingTicketsInput = {
      id: booking.id,
      ticketIDs: values.selectedTicketsIds,
      reason: values.reason as GTBookingTicketCancelationReason
    }

    if (comments) {
      input = {
        ...input,
        comments
      }
    }

    if (!isEmpty(values.refundDestination)) {
      input.refundDestination = values.refundDestination
    }
    setCancelTicketsV2Loading(true)
    cancelTicketsV2({
      variables: {
        input
      }
    })
  }
  const handleSubmit = (values: ICancellationData): void => {
    if (
      amountsAreMatching({
        booking,
        selectedPayments: values.selectedPayments,
        selectedTicketsIds: values.selectedTicketsIds
      })
    ) {
      cancellationData.current = values
      let input: GTCancelTicketsInput = {
        bookingID: booking.id,
        ticketIDs: values.selectedTicketsIds,
        reason: values.reason as GTBookingTicketCancelationReason
      }

      if (comments) {
        input = {
          ...input,
          comments
        }
      }

      const selectedPayments = values.selectedPayments.filter(
        ({ amountToRefund }): TSFixMe => round(amountToRefund) > 0
      )
      if (!isEmpty(selectedPayments)) {
        input.payments = selectedPayments.map((selectedPayment): TSFixMe => {
          return {
            paymentID: selectedPayment.paymentId,
            amount: sanitizePrice(selectedPayment.amountToRefund * 100)
          }
        })
      }

      cancelTickets({
        variables: {
          input
        }
      })
    }
  }

  const onSubmit = (values: ICancellationData): void => {
    if (isNewCancellationFlow) {
      handleSubmitV2(values)
    } else {
      delete values.refundDestination
      handleSubmit(values)
    }
  }

  const isTotalCancellationTabActive = tabIndex === CANCELLATION_TOTAL_INDEX
  const isPartialCancellationTabActive = tabIndex === CANCELLATION_PARTIAL_INDEX

  const initialValues: ICancellationData = mapBookingToCancelBookingForm(booking, isNewCancellationFlow)
  const canPartialCancel = booking.vertical !== GTBookingV2Vertical.Team
  const error = cancelTicketsStatus.error || cancelTicketsV2Status.error
  const loading = cancelTicketsStatus.loading || cancelTicketsV2Status.loading || cancelTicketsV2Loading
  return (
    <ModalContainer large>
      <ModalHeader>
        <Heading.H5>
          <FormattedMessage id="bookings.cancellation.title" defaultMessage="Cancel tickets" />
        </Heading.H5>
      </ModalHeader>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={schema(intl, booking, paidPayments, isNewCancellationFlow)}
      >
        {(props): ReactElement => {
          const hasTicketsError = props.touched.selectedTicketsIds && isString(props.errors.selectedTicketsIds)
          const hasPaymentsError = props.touched.selectedPayments && isString(props.errors.selectedPayments)
          const isShowingAmountsError =
            !isNewCancellationFlow &&
            !props.errors.selectedPayments &&
            !props.errors.selectedTicketsIds &&
            !isEmpty(props.values.selectedPayments) &&
            !isEmpty(props.values.selectedTicketsIds) &&
            !amountsAreMatching({
              booking,
              selectedPayments: props.values.selectedPayments,
              selectedTicketsIds: props.values.selectedTicketsIds
            })

          const selectRefundDestinationProps: ISelectRefundDestinationProps = {
            canRefundToKredits: booking.salesChannel?.id !== process.env.POS_MX_SALES_CHANNEL_ID,
            refundDestination: props.values.refundDestination ?? GTRefundDestinationInput.Card,
            setRefundDestination: (value): void => {
              if (isPartialCancellationTabActive) {
                resetPartialCancellationForm()
              }
              props.setFieldValue('refundDestination', value)
            },
            last4: paidPayments[0]?.last4 ?? '',
            paidPayments: paidPayments,
            bookingRefunds: booking.refunds
          }

          const onSelectTickets = (selectedTicketsIds: string[]): void => {
            props.setValues({
              ...props.values,
              selectedTicketsIds,
              selectedPayments: []
            })
          }

          const partialCancellationProps = {
            booking: booking,
            paidPayments: paidPayments,
            selectedTicketsIds: props.values.selectedTicketsIds,
            setSelectedTicketsIds: (selectedTicketsIds: string[]): void => {
              if (isNewCancellationFlow) {
                onSelectTickets(selectedTicketsIds)
                return
              }
              if (paidPayments.length === 1) {
                const selectedTickets = booking.tickets.filter(({ id }): TSFixMe => selectedTicketsIds.includes(id))
                const totalPaidAmountByTickets = selectedTickets.reduce((total, selectedTicket): TSFixMe => {
                  return total + selectedTicket.paidAmount
                }, 0)
                const selectedPayments = paidPayments.map((payment): TSFixMe => {
                  return {
                    paymentId: payment.id,
                    paidAmount: payment.amount,
                    amountToRefund: round(totalPaidAmountByTickets / 100, 2)
                  }
                })
                props.setValues({
                  ...props.values,
                  selectedTicketsIds,
                  selectedPayments
                })
              } else {
                props.setFieldValue('selectedTicketsIds', selectedTicketsIds)
              }
            },
            formProps: props
          }
          const onSelectPartialCancellationTab = (): void => {
            setTabIndex(CANCELLATION_PARTIAL_INDEX)
            props.setFieldValue('selectedTicketsIds', [])
            if (isNewCancellationFlow || paidPayments.length === 1) {
              props.setFieldValue(
                'selectedPayments',
                paidPayments.map((payment) => {
                  return {
                    processor: payment.processor,
                    paymentId: payment.id,
                    paidAmount: payment.amount,
                    amountToRefund: 0
                  }
                })
              )
            } else {
              props.setFieldValue('selectedPayments', [])
            }
            setComments('')
          }
          const resetPartialCancellationForm = (): void => {
            props.setFieldValue('selectedTicketsIds', [])
            props.setFieldValue('selectedPayments', [])
          }
          return (
            <form onSubmit={props.handleSubmit}>
              {canPartialCancel && (
                <Tabs>
                  <Tab
                    isActive={isTotalCancellationTabActive}
                    onClick={(): void => {
                      setTabIndex(CANCELLATION_TOTAL_INDEX)
                      props.setFieldValue('selectedTicketsIds', getUncancelledTicketsIds(booking))
                      if (!isEmpty(paidPayments)) {
                        props.setFieldValue(
                          'selectedPayments',
                          paidPayments.map((payment) => {
                            return {
                              processor: payment.processor,
                              paymentId: payment.id,
                              paidAmount: payment.amount,
                              amountToRefund: round(payment.amount / 100, 2)
                            }
                          })
                        )
                      }
                    }}
                  >
                    <FormattedMessage id="dictionary.total" defaultMessage="Total" />
                  </Tab>
                  <Tab isActive={isPartialCancellationTabActive} onClick={onSelectPartialCancellationTab}>
                    <FormattedMessage id="dictionary.partial" defaultMessage="Partial" />
                  </Tab>
                </Tabs>
              )}
              <Modal.Content>
                {isTotalCancellationTabActive && (
                  <Row>
                    <Column>
                      <TotalCancellation booking={booking} />
                    </Column>
                  </Row>
                )}
                {isNewCancellationFlow && isTotalCancellationTabActive && hasPaidPayments && (
                  <Row>
                    <Column>
                      <SelectRefundDestination {...selectRefundDestinationProps} />
                    </Column>
                  </Row>
                )}
                <Row>
                  <Column>
                    {isPartialCancellationTabActive && !isNewCancellationFlow && (
                      <PartialCancellation {...partialCancellationProps} />
                    )}

                    {isPartialCancellationTabActive && isNewCancellationFlow && (
                      <PartialCancellationV2 {...partialCancellationProps} {...selectRefundDestinationProps} />
                    )}
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Select
                      id="reason"
                      value={props.values.reason}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      touched={props.touched?.reason}
                      errors={props.errors?.reason}
                      label={<FormattedMessage id="dictionary.reason" defaultMessage="Reason" />}
                    >
                      <option value="">{intl.formatMessage(messages.reasonPlaceholder)}</option>
                      {CANCEL_BOOKING_REASONS.map((reason): TSFixMe => {
                        return (
                          <option key={reason} value={reason}>
                            {safeFormatMessage({ intl, messages: cancelBookingReasonMessages, key: reason })}
                          </option>
                        )
                      })}
                    </Select>
                  </Column>
                </Row>
                {isTotalCancellationTabActive && (
                  <Row>
                    <Column>
                      <Textarea
                        id="comments"
                        value={comments}
                        onChange={handleCommentsChange}
                        label={<FormattedMessage id="companies.new.form.comments.label" defaultMessage="Comments" />}
                      />
                    </Column>
                  </Row>
                )}
              </Modal.Content>
              <Modal.Footer>
                <Modal.ErrorMessage>
                  {hasTicketsError && isPartialCancellationTabActive && (
                    <ErrorMessage>{props.errors.selectedTicketsIds}</ErrorMessage>
                  )}
                  {hasPaymentsError && <ErrorMessage>{props.errors.selectedPayments as ReactNode}</ErrorMessage>}
                  {isShowingAmountsError && (
                    <ErrorMessage>
                      <FormattedMessage
                        id="booking.cancellation.matchingAmounts.required"
                        defaultMessage="The tickets amount and the refunds amount should be equals."
                      />
                    </ErrorMessage>
                  )}
                  {error && (
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="booking.cancellation.error"
                        defaultMessage="There was an error while cancelling the tickets"
                      />
                    </ErrorMessage>
                  )}
                </Modal.ErrorMessage>
                <Modal.Actions>
                  <Button kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="dictionary.close" defaultMessage="Close" />
                  </Button>
                  <Button kind="primary" type="submit" loading={loading}>
                    <FormattedMessage id="booking.cancellation.action" defaultMessage="Cancel tickets" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </ModalContainer>
  )
}
